// src/pages/settings/BillingTab.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  usePlan, // now = useBillingSummary under the hood
  useBillingInvoices,
  useOpenPortal,
  useBillingHealth,
  useResyncBillingHealth,
} from '../../hooks/useBillingQueries';
import api from '../../api/axios';
import {
  isSubscriptionReadOnly,
} from '../../services/billingService';

// ---------- helpers ----------

const StatusChip = ({ value }) => {
  if (!value) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-gray-100 text-gray-600 ring-gray-200">
        —
      </span>
    );
  }

  const normalized = String(value).toLowerCase();
  const map = {
    active: 'bg-green-50 text-green-700 ring-green-200',
    trialing: 'bg-blue-50 text-blue-700 ring-blue-200',
    past_due: 'bg-amber-50 text-amber-700 ring-amber-200',
    canceled: 'bg-gray-100 text-gray-600 ring-gray-200',
    incomplete: 'bg-rose-50 text-rose-700 ring-rose-200',
    incomplete_expired: 'bg-rose-50 text-rose-700 ring-rose-200',
    unpaid: 'bg-rose-50 text-rose-700 ring-rose-200',
    promo: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  };

  const cls =
    map[normalized] || 'bg-gray-100 text-gray-600 ring-gray-200';

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {normalized.replace('_', ' ')}
    </span>
  );
};

const ConnectStatusChip = ({ value, loading }) => {
  if (loading) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-slate-100 text-slate-500 ring-slate-200">
        Checking…
      </span>
    );
  }

  const normalized = String(value || 'not_connected').toLowerCase();

  let label = 'Not connected';
  let cls = 'bg-slate-100 text-slate-600 ring-slate-200';

  if (normalized === 'connected' || normalized === 'enabled') {
    label = 'Connected';
    cls = 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  } else if (normalized === 'pending') {
    label = 'Pending';
    cls = 'bg-amber-50 text-amber-700 ring-amber-200';
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
};

const HealthChip = ({ status }) => {
  const normalized = String(status || 'unknown').toLowerCase();

  let label = 'Unknown';
  let cls = 'bg-slate-100 text-slate-600 ring-slate-200';

  if (normalized === 'healthy') {
    label = 'Healthy';
    cls = 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  } else if (normalized === 'warning') {
    label = 'Warning';
    cls = 'bg-amber-50 text-amber-700 ring-amber-200';
  } else if (normalized === 'error') {
    label = 'Error';
    cls = 'bg-rose-50 text-rose-700 ring-rose-200';
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
};

function fmtMoneyFromCents(cents, currency = 'CAD') {
  const n =
    typeof cents === 'number' ? cents : Number(cents || 0);
  return (n / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  });
}

function toDate(value) {
  if (!value) return null;
  if (typeof value === 'number') {
    // Stripe timestamps are seconds; JS timestamps are ms
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  return new Date(value);
}

function fmtDate(value) {
  const d = toDate(value);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

const PLAN_LABELS = {
  starter: 'Starter',
  pro: 'Pro',
  advanced: 'Advanced',
};

// Small label map for business country (billingCountry)
const COUNTRY_LABELS = {
  CA: 'Canada',
  US: 'United States',
};

// Normalize backend /settings/payments (and toggle summary) into UI shape
const normalizePaymentSettingsResponse = (payload) => {
  const raw = (payload && (payload.settings || payload)) || {};

  const hasStripeConnectAccountId = !!raw.accountId;

  let stripeConnectStatus = 'not_connected';
  if (!raw.accountId) {
    stripeConnectStatus = 'not_connected';
  } else if (
    raw.connected &&
    raw.chargesEnabled &&
    raw.payoutsEnabled &&
    raw.detailsSubmitted
  ) {
    stripeConnectStatus = 'connected';
  } else {
    // Account exists but not fully ready
    stripeConnectStatus = 'pending';
  }

  return {
    ...raw,
    hasStripeConnectAccountId,
    stripeConnectStatus,
  };
};

const BillingTab = () => {
  const location = useLocation();

  const {
    data: planData,
    isLoading: loadingPlan,
    refetch: refetchPlan,
  } = usePlan(); // calls /billing/summary

  const { data: invoiceData, isLoading: loadingInvoices } =
    useBillingInvoices();

  const {
    mutate: openPortal,
    isPending: openingPortal,
  } = useOpenPortal({
    onSuccess: ({ url }) => {
      if (!url) return toast.error('Could not open billing portal.');
      window.location.assign(url);
    },
    onError: (e) => {
      console.error(e);
      toast.error('Failed to open billing portal.');
    },
  });

  // ---------- Billing health (Stripe ↔ DB sync) ----------

  const {
    data: healthData,
    isLoading: loadingHealth,
  } = useBillingHealth();

  // Backend shape:
  // {
  //   ok: boolean,
  //   level: 'ok' | 'warning' | 'error',
  //   checkedAt, reason,
  //   mismatches: [...],
  //   db, stripe
  // }
  const rawLevel = healthData?.level || null; // 'ok' | 'warning' | 'error' | null
  const healthStatus =
    rawLevel === 'ok'
      ? 'healthy'
      : rawLevel === 'warning' || rawLevel === 'error'
      ? rawLevel
      : null;

  const mismatches = Array.isArray(healthData?.mismatches)
    ? healthData.mismatches
    : [];

  const seatMismatch =
    mismatches.find(
      (m) =>
        m.code === 'SEATS_PURCHASED_MISMATCH' ||
        m.code === 'SEATS_OVER_LIMIT'
    ) || null;

  const healthSeatDelta =
    seatMismatch &&
    typeof seatMismatch.db === 'number' &&
    typeof seatMismatch.stripe === 'number'
      ? seatMismatch.stripe - seatMismatch.db
      : null;

  const healthMessage =
    healthStatus === 'healthy'
      ? 'Stripe and Asteriq billing are in sync.'
      : healthStatus === 'warning'
      ? 'There are differences between Stripe and Asteriq billing. Review seats, plan, or currency.'
      : healthStatus === 'error'
      ? 'Billing sync is currently failing. Check Stripe subscription and webhooks.'
      : null;

  const {
    mutate: resyncHealth,
    isPending: resyncingHealth,
  } = useResyncBillingHealth({
    onSuccess: (res) => {
      const level = res?.level || null;
      const status =
        level === 'ok'
          ? 'healthy'
          : level === 'warning' || level === 'error'
          ? level
          : null;

      if (status === 'healthy') {
        toast.success('Billing is now in sync with Stripe.');
      } else {
        toast.info('Billing health check completed.');
      }
    },
    onError: (err) => {
      console.error('[BillingTab] resyncHealth error', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to resync billing with Stripe.';
      toast.error(msg);
    },
  });

  // ---------- Online payments / Connect state ----------

  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loadingPaymentSettings, setLoadingPaymentSettings] =
    useState(false);
  const [savingPaymentSettings, setSavingPaymentSettings] =
    useState(false);
  const [creatingConnectLink, setCreatingConnectLink] =
    useState(false);
  const [reactivating, setReactivating] = useState(false);

  const onlinePaymentsEnabled =
    paymentSettings?.onlinePaymentsEnabled === true;
  const connectStatus =
    paymentSettings?.stripeConnectStatus || 'not_connected';
  const hasConnectAccount =
    paymentSettings?.hasStripeConnectAccountId === true;
  const chargesEnabled =
    paymentSettings?.chargesEnabled ?? null;
  const payoutsEnabled =
    paymentSettings?.payoutsEnabled ?? null;

  const canToggleOnline =
    hasConnectAccount &&
    chargesEnabled !== false &&
    payoutsEnabled !== false;

  // Fetch payment settings once
  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        setLoadingPaymentSettings(true);
        const res = await api.get('/settings/payments');
        if (!mounted) return;
        const settings = normalizePaymentSettingsResponse(res?.data);
        setPaymentSettings(settings);
      } catch (err) {
        console.error(
          '[BillingTab] Failed to load payment settings',
          err
        );
      } finally {
        if (mounted) setLoadingPaymentSettings(false);
      }
    };

    fetchSettings();
    return () => {
      mounted = false;
    };
  }, []);

  const handleConnectStripe = useCallback(async () => {
    try {
      setCreatingConnectLink(true);
      const res = await api.post('/settings/payments/connect-link');
      const url =
        res?.data?.url ||
        res?.data?.link ||
        res?.data?.redirectUrl;
      if (!url) {
        toast.error('Could not open Stripe Connect page.');
        return;
      }
      window.location.assign(url);
    } catch (err) {
      console.error(
        '[BillingTab] Failed to create connect link',
        err
      );
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to open Stripe Connect page.';
      toast.error(msg);
    } finally {
      setCreatingConnectLink(false);
    }
  }, []);

  const handleToggleOnlinePayments = useCallback(async () => {
    if (!paymentSettings) return;
    if (!canToggleOnline) {
      toast.info(
        'Complete Stripe Connect setup before enabling online payments.'
      );
      return;
    }

    const nextValue = !onlinePaymentsEnabled;

    try {
      setSavingPaymentSettings(true);
      const res = await api.post('/settings/payments/toggle', {
        onlinePaymentsEnabled: nextValue,
      });
      const payload = res?.data || {};
      // toggle endpoint returns { ok, onlinePaymentsEnabled, summary }
      const summary =
        payload.summary ||
        payload.settings ||
        payload;

      const normalized = normalizePaymentSettingsResponse(summary);
      setPaymentSettings(normalized);

      toast.success(
        nextValue
          ? 'Online payments enabled for customer invoices.'
          : 'Online payments disabled for customer invoices.'
      );
    } catch (err) {
      console.error(
        '[BillingTab] Failed to toggle online payments',
        err
      );
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to update online payments settings.';
      toast.error(msg);
    } finally {
      setSavingPaymentSettings(false);
    }
  }, [paymentSettings, canToggleOnline, onlinePaymentsEnabled]);

  // Toast + hard refresh of billing summary / payment settings
  // on return from Stripe (billing or Connect)
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const s = qs.get('status');
    const c = qs.get('connect');
    const stripeConnectParam = qs.get('stripe_connect');
    const stripeConnectPortalParam = qs.get(
      'stripe_connect_portal'
    );

    if (s === 'success') {
      toast.success('Billing updated successfully.');
      refetchPlan();
    } else if (s === 'canceled') {
      toast.info('Checkout canceled.');
    }

    const connectCompleted =
      c === 'connected' ||
      stripeConnectParam === '1' ||
      stripeConnectParam === 'true';

    const connectCanceled =
      c === 'canceled' ||
      stripeConnectParam === '0' ||
      stripeConnectParam === 'false';

    const returnedFromPortal =
      stripeConnectPortalParam === '1' ||
      stripeConnectPortalParam === 'true';

    const refreshPaymentSettings = async (toastMessage) => {
      try {
        if (toastMessage) toastMessage();
        setLoadingPaymentSettings(true);
        const res = await api.get('/settings/payments');
        const settings = normalizePaymentSettingsResponse(
          res?.data
        );
        setPaymentSettings(settings);
      } catch (err) {
        console.error(
          '[BillingTab] Failed to refresh payment settings after Stripe return',
          err
        );
      } finally {
        setLoadingPaymentSettings(false);
      }
    };

    if (connectCompleted) {
      refreshPaymentSettings(() =>
        toast.success('Stripe Connect setup completed.')
      );
    } else if (connectCanceled) {
      toast.info('Stripe Connect onboarding canceled.');
    } else if (returnedFromPortal) {
      // Came back from the Stripe Express dashboard
      refreshPaymentSettings(() =>
        toast.info('Returned from Stripe Connect.')
      );
    }
  }, [location.search, refetchPlan]);

  // ---------- Summary from /billing/summary ----------

  const summary = planData || {};

  const planId = (summary.plan || 'starter').toLowerCase();
  const planLabel =
    PLAN_LABELS[planId] ||
    planId.charAt(0).toUpperCase() + planId.slice(1);

  const promoActive = summary.promoActive === true;
  const promoExpiresAt = summary.promoExpiresAt || null;

  const rawBillingStatus =
    summary.billingStatus ||
    summary.stripe?.subscriptionStatus ||
    summary.status ||
    null;
  const billingStatus = rawBillingStatus
    ? String(rawBillingStatus).toLowerCase()
    : null;

  const billingCurrency = (
    summary.billingCurrency || 'CAD'
  ).toUpperCase();
  const billingEmail =
    summary.billingEmail ||
    summary.email ||
    summary.companyEmail ||
    null;

  // NEW: business country (billingCountry) from summary
  const billingCountryRaw =
    summary.billingCountry ||
    summary.country ||
    summary.companyCountry ||
    null;
  const billingCountry = billingCountryRaw
    ? String(billingCountryRaw).toUpperCase()
    : null;
  const hasBillingCountry = !!billingCountry;

  const seatsPurchased = summary.seatsPurchased ?? 1;
  const seatsInUse = summary.seatsInUse ?? 1;

  const trialEndsAt = summary.trialEndsAt || null;

  const subscriptionStatus =
    summary.stripe?.subscriptionStatus || null;
  const currentPeriodEnd =
    summary.stripe?.currentPeriodEnd || null;
  const stripeCustomerId =
    summary.stripe?.customerId || null;

  const ACTIVE_PORTAL_STATUSES = [
    'active',
    'trialing',
    'past_due',
    'unpaid',
  ];
  const REACTIVATABLE_STATUSES = [
    'canceled',
    'incomplete',
    'incomplete_expired',
  ];

  const canOpenPortal =
    !!stripeCustomerId &&
    (billingStatus
      ? ACTIVE_PORTAL_STATUSES.includes(billingStatus)
      : subscriptionStatus
      ? ACTIVE_PORTAL_STATUSES.includes(
          String(subscriptionStatus).toLowerCase()
        )
      : false);

  const isReactivatable =
    billingStatus &&
    REACTIVATABLE_STATUSES.includes(billingStatus);

  const isPurePromo =
    promoActive &&
    !summary.stripe?.subscriptionId &&
    !subscriptionStatus;

  const showPortalButton =
    !loadingPlan && canOpenPortal && !isPurePromo;

  const showReactivateButton =
    !loadingPlan && (isReactivatable || isPurePromo);

  const readOnly =
    promoActive
      ? false
      : billingStatus &&
        typeof isSubscriptionReadOnly === 'function'
      ? isSubscriptionReadOnly(billingStatus)
      : false;

  const invoices = useMemo(() => {
    if (Array.isArray(invoiceData)) return invoiceData;
    if (
      invoiceData?.invoices &&
      Array.isArray(invoiceData.invoices)
    ) {
      return invoiceData.invoices;
    }
    return [];
  }, [invoiceData]);

  const isTogglingOnlinePayments =
    savingPaymentSettings || loadingPaymentSettings;

  // Smart label for the Connect / Portal button
  let connectButtonLabel = 'Connect Stripe';
  if (!hasConnectAccount) {
    connectButtonLabel = creatingConnectLink
      ? 'Connecting…'
      : 'Connect Stripe';
  } else if (connectStatus === 'connected') {
    connectButtonLabel = creatingConnectLink
      ? 'Opening Stripe portal…'
      : 'Open Stripe payouts portal';
  } else {
    // account exists but pending
    connectButtonLabel = creatingConnectLink
      ? 'Opening Stripe…'
      : 'Continue Stripe onboarding';
  }

  const showHealthBanner =
    !loadingHealth &&
    (healthStatus === 'warning' || healthStatus === 'error');

  const handleReactivate = useCallback(async () => {
    try {
      setReactivating(true);
      const body = {
        plan: planId || 'starter',
        seats: seatsPurchased || 1,
        currency: billingCurrency,
      };
      const res = await api.post('/billing/checkout', body);
      const url =
        res?.data?.url ||
        res?.data?.checkoutUrl ||
        res?.data?.stripe?.checkoutUrl;
      if (!url) {
        toast.error(
          'Could not start Stripe checkout to reactivate your subscription.'
        );
        return;
      }
      toast.success(
        'Redirecting to Stripe to reactivate your subscription…'
      );
      window.location.assign(url);
    } catch (err) {
      console.error(
        '[BillingTab] Reactivate checkout failed',
        err
      );
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to start reactivation checkout.';
      toast.error(msg);
    } finally {
      setReactivating(false);
    }
  }, [planId, seatsPurchased, billingCurrency]);

  return (
    <div className="space-y-8">
      {/* Health banner: Stripe ↔ DB sync */}
      {showHealthBanner && (
        <div
          className={`flex flex-col gap-2 rounded-2xl border px-4 py-3 text-xs sm:flex-row sm:items-center sm:justify-between ${
            healthStatus === 'error'
              ? 'border-rose-200 bg-rose-50 text-rose-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          <div className="flex items-start gap-2">
            <HealthChip status={healthStatus} />
            <div>
              <p className="font-medium">
                {healthStatus === 'error'
                  ? 'Billing sync issue detected'
                  : 'Billing mismatch detected'}
              </p>
              {healthMessage && (
                <p className="mt-0.5">
                  {healthMessage}
                  {typeof healthSeatDelta === 'number' &&
                    healthSeatDelta !== 0 && (
                      <>
                        {' '}
                        Currently Stripe shows a different seat
                        count than Asteriq.
                      </>
                    )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => resyncHealth({})}
              disabled={resyncingHealth}
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-60"
            >
              {resyncingHealth ? 'Checking…' : 'Fix / retry sync'}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Billing & subscription
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage your plan, seats, Stripe invoices, and
            online payments for this workspace.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {billingStatus && (
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 border border-slate-200">
              <span className="text-slate-400">Status</span>
              <StatusChip value={billingStatus} />
            </div>
          )}
          {!loadingHealth && healthStatus && (
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 border border-slate-200">
              <span className="text-slate-400">Sync</span>
              <HealthChip status={healthStatus} />
            </div>
          )}

          {showPortalButton && (
            <button
              type="button"
              onClick={() => openPortal({})}
              disabled={openingPortal}
              className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60"
            >
              {openingPortal ? 'Opening portal…' : 'Manage in Stripe'}
            </button>
          )}

          {showReactivateButton && (
            <button
              type="button"
              onClick={handleReactivate}
              disabled={reactivating}
              className="inline-flex items-center justify-center rounded-lg border border-emerald-600 bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {reactivating
                ? 'Redirecting…'
                : isPurePromo
                ? 'Start subscription'
                : 'Reactivate subscription'}
            </button>
          )}
        </div>
      </div>

      {/* Top grid: Plan + Seats + Billing details */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Plan card */}
        <section className="col-span-1 rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                Plan
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Current subscription tier and billing status
                for this company.
              </p>
            </div>
            <StatusChip value={billingStatus || (promoActive ? 'promo' : null)} />
          </div>

          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-lg font-semibold text-slate-900 capitalize">
              {loadingPlan ? 'Loading…' : planLabel}
            </span>
            {subscriptionStatus && (
              <span className="text-xs text-slate-500">
                ({subscriptionStatus})
              </span>
            )}
          </div>

          <p className="mt-2 text-xs text-slate-500">
            Billed in{' '}
            <span className="font-medium">
              {billingCurrency}
            </span>
            .
          </p>

          {currentPeriodEnd && !isPurePromo && (
            <p className="mt-2 text-xs text-slate-500">
              Current period ends on{' '}
              <span className="font-medium">
                {fmtDate(currentPeriodEnd)}
              </span>
              .
            </p>
          )}

          {trialEndsAt && (
            <p className="mt-3 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
              Trial ends on{' '}
              <span className="font-semibold">
                {fmtDate(trialEndsAt)}
              </span>
              . Make sure your payment method is up to date
              in Stripe.
            </p>
          )}

          {promoActive && (
            <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
              Promo access
              {promoExpiresAt && (
                <>
                  {' '}
                  until{' '}
                  <span className="font-semibold">
                    {fmtDate(promoExpiresAt)}
                  </span>
                </>
              )}
              . You can use Asteriq without a paid
              subscription while this promo is active.
            </p>
          )}

          {readOnly && !promoActive && (
            <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-[11px] text-rose-800">
              Your subscription is inactive. Asteriq is in
              read-only mode. Reactivate your subscription in
              Stripe to resume full access.
            </p>
          )}
        </section>

        {/* Seats card */}
        <section className="col-span-1 rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Seats
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Owners, admins, and staff consume seats. Viewers
            are free.
          </p>

          <div className="mt-4 flex items-baseline gap-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                In use
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {seatsInUse}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Purchased
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {seatsPurchased}
              </p>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            Seat changes are managed via the Stripe portal
            for now. Asteriq will auto-sync usage and
            billing quantity.
          </p>

          {typeof healthSeatDelta === 'number' &&
            healthSeatDelta !== 0 && (
              <p className="mt-2 text-[11px] text-amber-700">
                Stripe currently reports a different seat
                quantity than Asteriq. Use the{' '}
                <span className="font-semibold">
                  “Fix / retry sync”
                </span>{' '}
                button above if this doesn&apos;t look right.
              </p>
            )}
        </section>

        {/* Billing details card */}
        <section className="col-span-1 rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Billing details
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            High-level billing info synced from your Stripe
            subscription.
          </p>

          <dl className="mt-4 space-y-3 text-xs">
            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-500">Billing email</dt>
              <dd className="max-w-[200px] text-right">
                {billingEmail ? (
                  <span className="truncate text-slate-900 inline-block align-top">
                    {billingEmail}
                  </span>
                ) : (
                  <span className="text-slate-400">Not set</span>
                )}
              </dd>
            </div>

            {/* NEW: Business country */}
            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-500">Business country</dt>
              <dd className="text-right">
                {billingCountry ? (
                  <span className="text-slate-900 font-medium">
                    {COUNTRY_LABELS[billingCountry] || billingCountry}
                  </span>
                ) : (
                  <span className="text-slate-400">
                    Not set (update in Company profile)
                  </span>
                )}
              </dd>
            </div>

            <div className="flex items-start justify-between gap-3">
              <dt className="text-slate-500">Currency</dt>
              <dd className="text-slate-900 font-medium">
                {billingCurrency}
              </dd>
            </div>

            {currentPeriodEnd && !isPurePromo && (
              <div className="flex items-start justify-between gap-3">
                <dt className="text-slate-500">Next renewal</dt>
                <dd className="text-slate-900">
                  {fmtDate(currentPeriodEnd)}
                </dd>
              </div>
            )}

            {stripeCustomerId && (
              <div className="flex items-start justify-between gap-3">
                <dt className="text-slate-500">
                  Stripe customer
                </dt>
                <dd className="text-slate-900">
                  …{String(stripeCustomerId).slice(-6)}
                </dd>
              </div>
            )}
          </dl>

          <p className="mt-4 text-[11px] text-slate-500">
            Use the Stripe portal to update billing email,
            card details, tax information, and subscription
            settings.
          </p>
        </section>
      </div>

      {/* Online payments / Stripe Connect */}
      <section className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Online payments (Stripe Connect)
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Let your customers pay invoices online using
              cards and wallets. Each company connects its
              own Stripe account; Asteriq does not take a
              cut.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>Funds go directly to your Stripe.</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                <span>No extra Asteriq payment fees.</span>
              </span>
            </div>

            {!hasBillingCountry && (
              <p className="mt-3 max-w-sm text-[11px] text-amber-700">
                Set your business country in Settings → Company
                profile before connecting Stripe. This is
                required by Stripe.
              </p>
            )}
          </div>

          <div className="flex flex-col items-start gap-3 md:items-end">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-600">
                Stripe Connect
              </span>
              <ConnectStatusChip
                value={connectStatus}
                loading={loadingPaymentSettings}
              />
            </div>

            {!hasConnectAccount ? (
              <button
                type="button"
                onClick={handleConnectStripe}
                disabled={
                  !hasBillingCountry ||
                  creatingConnectLink ||
                  loadingPaymentSettings
                }
                className={`inline-flex items-center justify-center rounded-lg border px-3.5 py-2 text-xs font-semibold shadow-sm disabled:opacity-60 ${
                  !hasBillingCountry
                    ? 'border-slate-300 bg-slate-100 text-slate-500 cursor-not-allowed'
                    : 'border-indigo-500 bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {connectButtonLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConnectStripe}
                disabled={
                  creatingConnectLink || loadingPaymentSettings
                }
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-xs font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {connectButtonLabel}
              </button>
            )}

            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="font-medium">
                Online payments
              </span>
              <button
                type="button"
                onClick={handleToggleOnlinePayments}
                disabled={
                  !canToggleOnline || isTogglingOnlinePayments
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors ${
                  onlinePaymentsEnabled
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'bg-slate-200 border-slate-300'
                } ${
                  !canToggleOnline || isTogglingOnlinePayments
                    ? 'opacity-60 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    onlinePaymentsEnabled
                      ? 'translate-x-5'
                      : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-slate-500">
                {onlinePaymentsEnabled
                  ? 'Enabled for customer invoices'
                  : 'Disabled'}
              </span>
            </div>

            {!canToggleOnline && hasConnectAccount && (
              <p className="max-w-xs text-[11px] text-amber-600">
                Your Stripe account is connected but not yet
                fully enabled for charges or payouts.
                Complete onboarding in Stripe before enabling
                online payments.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Billing history / invoices */}
      <section className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Stripe invoices
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Full invoice details and downloads are available
              in the Stripe portal.
            </p>
          </div>
        </div>

        {loadingInvoices ? (
          <p className="mt-4 text-xs text-slate-500">
            Loading invoices…
          </p>
        ) : invoices.length === 0 ? (
          <p className="mt-4 text-xs text-slate-500">
            No invoices found for this customer yet.
          </p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-100">
            <div className="max-h-80 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">
                      Invoice
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide">
                      Amount
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {invoices.map((inv) => {
                    const amountCents =
                      inv.total ??
                      inv.amountDue ??
                      inv.amount_due ??
                      0;
                    const currency =
                      inv.currency || billingCurrency;
                    const label = inv.number || inv.id;
                    return (
                      <tr key={inv.id}>
                        <td className="px-3 py-2 text-slate-900">
                          {label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {fmtDate(inv.created)}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-900">
                          {fmtMoneyFromCents(
                            amountCents,
                            currency
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600 capitalize">
                          {inv.status || '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          <div className="inline-flex gap-2">
                            {inv.hostedInvoiceUrl && (
                              <a
                                href={inv.hostedInvoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-700 hover:text-slate-900 underline underline-offset-2"
                              >
                                View
                              </a>
                            )}
                            {inv.invoicePdf && (
                              <a
                                href={inv.invoicePdf}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-700 hover:text-slate-900 underline underline-offset-2"
                              >
                                PDF
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default BillingTab;
